import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import { flattenRoutes, perspectiveAppRoutes } from './routes/routeConfig';
import Layout from './components/Layout';
import { useStore } from '../store';
import PrivateRoute from './components/PrivateRoute';
import MobileGuard from './pages/mobile/MobileGuard';
import GlobalToast from './components/GlobalToast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { GlobalErrorHandlerInitializer } from './components/GlobalErrorHandlerInitializer';
import { useEffect, Suspense, lazy } from 'react';
import './i18n';

const MobileSetupPage = lazy(() => import('./pages/mobile/SetupPage'));
const MobileResourceListPage = lazy(() => import('./pages/mobile/ResourceListPage'));
const MobileResourceDetailPage = lazy(() => import('./pages/mobile/ResourceDetailPage'));
import { useTranslation } from 'react-i18next';
import { hydrateAuthStore } from '../store/hydration';
import { hasPermission, hasAnyPermission } from './utils/permissions';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { devFeaturesEnabled, isDevOnlyRoute } from './routes/devOnlyRoutes';
import { isModuleLicensed } from './utils/license';
import { perspectives } from '../constants';

const NotLicensedPage = lazy(() => import('./pages/NotLicensedPage'));

function App() {
  const { i18n } = useTranslation();
  const userProfile = useStore((s) => s.userProfile);
  const userRole = userProfile?.role || 'guest';
  const userPermissions = userProfile?.permissions || [];
  const hydrated = useStore((s) => s.hydrated);
  const isAuthenticated = useStore((s) => s.isAuthenticated);

  const rebuildTree = useStore((s) => s.rebuildTree);

  // Set up automatic token refresh
  useTokenRefresh();

  // Check if we have tokens but no user profile (hydration issue)
  useEffect(() => {
    if (hydrated && isAuthenticated() && !userProfile) {
      // Re-trigger hydration
      hydrateAuthStore().catch(error => {
        console.error('Re-hydration failed:', error);
      });
    }
  }, [hydrated, isAuthenticated, userProfile]);

  // Rebuild tree when user profile becomes available (only if no tree exists)
  useEffect(() => {
    if (hydrated && userProfile && isAuthenticated()) {
      const { treeData } = useStore.getState();
      if (!treeData || treeData.length === 0) {
        rebuildTree();
      }
    }
  }, [hydrated, userProfile, isAuthenticated, rebuildTree]);

  // Initialize language from localStorage or browser
  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  // Mapping from nodeId to entity name for permission checking.
  // This should match the mapping in treeBuilder.ts
  const nodeIdToEntityMap: Record<string, string | null> = {
    // Order Data
    "2.1": "productionorder", // Production Orders
    "2.2": "demand", // Demand
    "2.3": "productionschedule:optimize", // Production Order Optimization
    
    // Material Data
    "3.1": "material", // Products
    "3.1.5": "material", // Intermediate Products
    "3.2": "material", // Raw Materials
    "3.3": "stocklevel", // Stocks
    "3.4": "delivery", // Deliveries
    
    // Resource Data
    "4.1": "resourcetype", // Resource Types
    "4.2": "resource", // Resources
    "4.3": "maintenancewindow", // Maintenance Windows
    "4.4": "shifttemplate", // Shift Model Templates
    
    // Routing Data
    "5.1": "routing-product", // Product Routings
    "5.1.5": "routing-intermediate", // Intermediate Routings
    "5.2": "setuptime", // Setup Times
    "5.3": "setuptime", // Oven Setup Times
    "5.4": "setuptime", // Processing Line Setup Times
    
    // Settings
    "6.1.1": "system:settings", // System Settings
    "6.1.2": "user", // User Management (employees + technical users via tabs)
    "6.1.3": "role", // User Roles
    "6.1.4": "holiday", // Holidays
    "6.1.5": "data:export|data:import", // Data Management
    "6.1.6": "gantt-profile", // Gantt Visualization Profiles
    "6.1.7": "system:settings", // CSV Import Config
    "6.1.8": "system:settings", // Excel Export Config
    "6.1.9": "system:settings", // Licence
    "6.1.10": "demo:tenant:reset", // Demo areas (Prod demo server only)
    "6.2.1": null, // Display Options (no specific permission)
    "6.2.2": null, // Coloring (no specific permission)
    "6.3.1": null, // Debug Test Functions (no specific permission)
    "6.3.2": null, // Browser Info (no specific permission)
    "6.3.3": "data:backup|data:restore", // Backup Restore
    "6.3.4": null, // Gantt (no specific permission)
    
    // Permissions
    "7": "permission", // Permissions
    
    // Scheduling
    "8": "scheduling", // Shift Plan
    "10": "productionschedule", // Production Schedules
    
    // Planning
    "11": "planning", // Production Plan Visualization
    "12": "campaignsequence", // Campaign Sequences
  };

  const resolveRoutePermissions = (entityName: string): string[] => {
    return entityName.split('|').map((name) => {
      const segments = name.split(':');
      if (segments.length >= 3) {
        return name;
      }
      if (segments.length === 2) {
        return `${name}:view`;
      }
      return `${name}:view`;
    });
  };

  // Helper function to check if user has access to a route
  // This should match the logic in treeBuilder.ts
  const hasRouteAccess = (route: any) => {
    if (isDevOnlyRoute(route.nodeId) && !devFeaturesEnabled) {
      return false;
    }
    // The demo areas page exists only on the Prod demo server.
    if (route.nodeId === "6.1.10" && !userProfile?.demoServer) {
      return false;
    }
    // Superadmin has access to everything
    if (userProfile?.superAdmin || userRole === 'superadmin') {
      return true;
    }
    
    // Check if route has an entity mapping
    const entityName = nodeIdToEntityMap[route.nodeId];
    
    if (entityName) {
      const permissionsToCheck = resolveRoutePermissions(entityName);
      if (permissionsToCheck.length === 1) {
        return hasPermission(permissionsToCheck[0], userProfile);
      }
      return hasAnyPermission(permissionsToCheck, userProfile);
    } else {
      // For routes without entity mapping, use old permission check (viewId) or admin role
      return (
        userPermissions.some(permission => 
          permission.viewId === route.nodeId && permission.read
        ) ||
        // Allow access to shift plan for admin users
        (route.nodeId === "8" && userRole === 'admin')
      );
    }
  };

  if (!hydrated) {
    // Show loading indicator while hydrating
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const filteredRoutes = flattenRoutes(perspectiveAppRoutes).filter(hasRouteAccess);
  // Perspectives whose module is not licensed: any URL below them shows the "not unlocked" page.
  const unlicensedPerspectives = perspectives.filter(
    (p) => p.module && !isModuleLicensed(userProfile, p.module),
  );

  return (
    <>
      <GlobalToast />
      <Router>
        <GlobalErrorHandlerInitializer />
        <Routes>
          {/* Public login route */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Mobile shopfloor view (separate auth path via QR-bootstrap, no
              tree/sidebar/perspective switcher). */}
          <Route
            path="/m/setup"
            element={
              <Suspense fallback={<div className="flex items-center justify-center min-h-dvh">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"/>
              </div>}>
                <MobileSetupPage />
              </Suspense>
            }
          />
          <Route
            path="/m"
            element={
              <MobileGuard>
                <Suspense fallback={<div className="flex items-center justify-center min-h-dvh">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"/>
                </div>}>
                  <ErrorBoundary>
                    <Outlet />
                  </ErrorBoundary>
                </Suspense>
              </MobileGuard>
            }
          >
            <Route index element={<MobileResourceListPage />} />
            <Route path="r/:resourceId" element={<MobileResourceDetailPage />} />
          </Route>

          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>}>
                    <ErrorBoundary>
                      <Routes>
                        {filteredRoutes
                          .map((r) => (
                            <Route
                              key={r.path}
                              path={r.path}
                              element={isModuleLicensed(userProfile, r.module)
                                ? <r.component/>
                                : <NotLicensedPage module={r.module!}/>}
                            />
                          ))}
                        {unlicensedPerspectives.map((p) => (
                          <Route
                            key={p.key}
                            path={`/${p.key}/*`}
                            element={<NotLicensedPage module={p.module!}/>}
                          />
                        ))}
                      </Routes>
                    </ErrorBoundary>
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;
